/**
 * A SÉRIE DO OXIGÊNIO — o que o gráfico precisa saber antes de ser desenhado.
 *
 * Pedido do gestor em 22/09/2026, e a razão dele é a especificação inteira:
 *
 *   «fazer a leitura de oxigênio e receber a notícia de que o oxigênio está
 *    caindo ou estável ou subindo, mas sem olhar para os dados anteriores é
 *    algo que fica 'tenho que acreditar mas não estou vendo de onde isso vem'»
 *
 * É o mesmo defeito do trato repetido do T-8 e do botão de anular sem sinal:
 * **o app afirma e não mostra de onde tirou**. Aqui a correção não é um aviso
 * — é a série.
 *
 * As contas moram aqui, e não no componente, pelo motivo de sempre: é conta, e
 * conta se testa. O componente só traduz `x` e `y` em SVG.
 *
 * ── DUAS REGRAS QUE FAZEM O GRÁFICO NÃO MENTIR ───────────────────────────
 *
 * 1. **O eixo vertical começa no ZERO.** Oxigênio é quantidade absoluta, e o
 *    corte do app acontece em valores absolutos — 2 e 3 mg/L. Começando o eixo
 *    em 2,8 para «aproveitar a altura», uma queda de 3,2 para 2,9 vira um
 *    precipício na tela. Quem olha decide pelo desenho, não pelo número.
 *
 * 2. **O eixo horizontal é por DATA, não por leitura.** Espaçando por posição,
 *    dez dias sem medir viram um passo igual ao do dia seguinte — e uma queda
 *    lenta de duas semanas ganha a cara de um tombo de um dia. O buraco na
 *    série é informação: ele mostra que ninguém mediu.
 *
 * ── E AS FAIXAS DO PRÓPRIO APP ENTRAM NO DESENHO ─────────────────────────
 *
 * O gráfico não mostra só a linha: mostra as faixas que produziram a
 * recomendação — abaixo de 2, de 2 a 3. É isso que responde «de onde isso
 * vem», e é o que separa este gráfico de um enfeite. Elas vêm da tabela do
 * banco, nunca daqui: outra piscicultura tem outra água.
 */

import type { FaixaDeCoeficiente } from '../acesso/api'
import { diasEntreDatas } from './unidades'

export interface LeituraDaSerie {
  data: string
  hora: string | null
  oxigenio_mgl: number | null
}

export interface PontoDaSerie {
  data: string
  valor: number
  /** 0 a 1 na horizontal, proporcional à DATA. */
  x: number
  /** 0 a 1 na vertical, já invertido para SVG: 0 é o topo. */
  y: number
}

export interface Banda {
  /** Onde a faixa começa e termina, em mg/L. */
  de: number
  ate: number
  /** O que o app faz nela: 0,5 quer dizer meia ração. */
  coeficiente: number
  /** 0 a 1, já em coordenada de SVG — `y` é o topo da banda. */
  y: number
  altura: number
}

export interface Serie {
  pontos: PontoDaSerie[]
  bandas: Banda[]
  /** O teto do eixo vertical, em mg/L. O piso é sempre zero. */
  teto: number
  /** As marcas do eixo vertical, de baixo para cima. */
  marcas: number[]
  primeiro: string
  ultimo: string
  /** Quantos dias a série cobre. Zero quando tudo é do mesmo dia. */
  dias: number
}

/** Menos que isto não é série: é um ponto, e um ponto não tem direção. */
export const MINIMO_DE_LEITURAS = 2

/**
 * UMA LEITURA POR DIA, E É A PRIMEIRA.
 *
 * Recomendação do gestor em 22/09/2026, depois de ver o gráfico: «ontem foram
 * feitas 2 leituras, a primeira deu baixa, a segunda no mesmo dia mais tarde
 * deu mais alta — a minha recomendação é usar somente a primeira».
 *
 * Ele tem razão, e o motivo é mais forte do que «discrepância entre dados».
 * O oxigênio tem ciclo diário: cai a noite inteira, com o peixe e a água
 * respirando e nada fazendo fotossíntese, e sobe durante o dia com o sol. Um
 * ponto das 6h ao lado de um das 16h não compara tanque com tanque — compara
 * manhã com tarde. **A inclinação da linha passaria a medir a hora em que
 * alguém mediu**, e uma represa lida às 6h na segunda e às 16h na terça
 * pareceria ter melhorado sem nada ter acontecido.
 *
 * E é a PRIMEIRA, e não a média nem a última, porque é ela que importa: o
 * mínimo do dia é o que aperta o peixe, e é a leitura da ronda, que acontece
 * sempre no mesmo horário. Média misturaria de novo o que a regra separa.
 *
 * ISTO NÃO APAGA LEITURA NENHUMA. As outras do dia continuam na tela da Ronda,
 * uma a uma — foi para elas caberem que a tela mudou em 21/09. O que muda é só
 * o que entra no GRÁFICO, que é uma pergunta sobre dias e não sobre medições.
 */
function primeiraDeCadaDia(leituras: readonly LeituraDaSerie[]): LeituraDaSerie[] {
  const porDia = new Map<string, LeituraDaSerie>()
  for (const l of leituras) {
    const dia = l.data.slice(0, 10)
    const atual = porDia.get(dia)
    if (atual === undefined || maisCedo(l, atual)) porDia.set(dia, l)
  }
  return [...porDia.values()]
}

/**
 * Qual das duas veio primeiro no dia.
 *
 * SEM HORA GANHA DE COM HORA, e não é descuido: leitura sem hora, neste
 * projeto, veio de ficha de papel — e ficha de papel é a ronda da manhã. Tratar
 * a que não tem hora como se fosse da tarde poria a mais matinal de todas em
 * último lugar.
 */
function maisCedo(a: LeituraDaSerie, b: LeituraDaSerie): boolean {
  if (a.hora === null) return true
  if (b.hora === null) return false
  return a.hora < b.hora
}

/**
 * Monta a série a partir das leituras e das faixas do banco.
 *
 * Recebe as leituras na ordem em que o servidor manda — da mais recente para a
 * mais antiga — e devolve na ordem do tempo, que é a de quem lê um gráfico.
 *
 * Devolve nulo quando não há o que desenhar. É esse nulo que faz o botão do
 * gráfico **não existir** numa piscicultura sem oxímetro, que foi a regra que
 * o gestor deu junto com o pedido — gráfico vazio é pior que botão ausente,
 * porque promete uma informação que não existe.
 */
export function montarSerie(
  leituras: readonly LeituraDaSerie[],
  faixas: readonly FaixaDeCoeficiente[],
  maximoDeDias = 14,
): Serie | null {
  const comOxigenio = primeiraDeCadaDia(
    leituras.filter((l): l is LeituraDaSerie & { oxigenio_mgl: number } =>
      typeof l.oxigenio_mgl === 'number' && Number.isFinite(l.oxigenio_mgl)),
  )
    // O corte é por DIA, e as leituras chegam do mais novo para o mais antigo.
    .slice(0, maximoDeDias)
    // Do mais antigo para o mais novo: o tempo anda para a direita.
    .reverse() as (LeituraDaSerie & { oxigenio_mgl: number })[]

  if (comOxigenio.length < MINIMO_DE_LEITURAS) return null

  const primeiroItem = comOxigenio[0]
  const ultimoItem = comOxigenio[comOxigenio.length - 1]
  if (primeiroItem === undefined || ultimoItem === undefined) return null

  const primeiro = primeiroItem.data.slice(0, 10)
  const ultimo = ultimoItem.data.slice(0, 10)
  const dias = diasEntreDatas(primeiro, ultimo)

  // AS FAIXAS DE OXIGÊNIO QUE CORTAM. Faixa de coeficiente 1 não é banda: ela
  // é «água boa», e pintar a tela inteira de faixa não distingue nada.
  const doOxigenio = faixas
    .filter((f) => f.fator === 'oxigenio_mgl' && f.coeficiente < 1)
    .map((f) => ({ de: f.de, ate: f.ate, coeficiente: f.coeficiente }))

  const maiorLeitura = Math.max(...comOxigenio.map((l) => l.oxigenio_mgl))
  const maiorBanda = doOxigenio.reduce((m, f) => Math.max(m, f.ate ?? f.de), 0)
  // Uma casa acima do maior valor, para o ponto não encostar no teto, e nunca
  // abaixo da maior faixa — senão a banda que explica o corte fica de fora.
  const teto = Math.max(Math.ceil(maiorLeitura + 1), Math.ceil(maiorBanda + 1), 4)

  const emY = (valor: number) => 1 - Math.min(1, Math.max(0, valor / teto))

  const bandas: Banda[] = doOxigenio.map((f) => {
    // Faixa sem teto vai até o topo do gráfico; faixa sem piso começa no zero.
    const ate = f.ate === null ? teto : Math.min(f.ate, teto)
    const de = Math.max(0, Math.min(f.de, teto))
    return {
      de,
      ate,
      coeficiente: f.coeficiente,
      y: emY(ate),
      altura: Math.max(0, emY(de) - emY(ate)),
    }
  })

  const pontos: PontoDaSerie[] = comOxigenio.map((l, i) => ({
    data: l.data.slice(0, 10),
    valor: l.oxigenio_mgl,
    // DIVISÃO POR ZERO, e nada além disso. Com uma leitura por dia, dois
    // pontos no mesmo dia deixaram de existir — `dias` só chega a zero com um
    // ponto só, que a linha do MINIMO já recusou. A guarda fica porque o custo
    // é uma linha e o estrago seria `Infinity` virando coordenada.
    x: dias > 0
      ? diasEntreDatas(primeiro, l.data.slice(0, 10)) / dias
      : comOxigenio.length === 1 ? 0 : i / (comOxigenio.length - 1),
    y: emY(l.oxigenio_mgl),
  }))

  // As marcas do eixo saem das faixas, e não de uma régua bonita: são elas
  // que o leitor precisa achar. O teto entra para o eixo ter fim.
  const marcas = [...new Set([0, ...doOxigenio.flatMap((f) => [f.de, f.ate ?? teto]), teto])]
    .filter((v) => v >= 0 && v <= teto)
    .sort((a, b) => a - b)

  return { pontos, bandas, teto, marcas, primeiro, ultimo, dias }
}
